import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PlusIcon, PencilSimpleIcon, TrashIcon, XIcon, FlaskIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const empty = { codigo: "", nombre: "", descripcion: "", activo: true };

function SunImage() {
  const rays = Array.from({ length: 12 }, (_, i) => i * 30);
  return (
    <svg viewBox="0 0 200 200" className="w-48 h-48" role="img" aria-label="Sol" data-testid="prueba-sol">
      <g stroke="#FAD22C" strokeWidth="8" strokeLinecap="round">
        {rays.map((deg) => (
          <line key={deg} x1="100" y1="18" x2="100" y2="42" transform={`rotate(${deg} 100 100)`} />
        ))}
      </g>
      <circle cx="100" cy="100" r="44" fill="#FAD22C" />
      <circle cx="100" cy="100" r="44" fill="none" stroke="#F5A524" strokeWidth="4" />
      <circle cx="84" cy="94" r="5" fill="#3a2a00" />
      <circle cx="116" cy="94" r="5" fill="#3a2a00" />
      <path d="M80 112 Q100 130 120 112" fill="none" stroke="#3a2a00" strokeWidth="5" strokeLinecap="round" />
    </svg>
  );
}

export default function Prueba() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showSun, setShowSun] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prueba");
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/prueba/${editing.id}`, { ...editing, ...form });
        toast.success("Registro actualizado");
      } else {
        await api.post("/prueba", form);
        toast.success("Registro creado");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const onEdit = (p) => { setEditing(p); setForm(p); setOpen(true); };
  const onDel = async (p) => {
    if (!window.confirm(`¿Eliminar el registro ${p.codigo}?`)) return;
    try { await api.delete(`/prueba/${p.id}`); toast.success("Registro eliminado"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };

  const pm = useModalForm({ value: form, abierto: open, onClose: () => setOpen(false), onSave: save });
  return (
    <Layout
      title="Prueba"
      subtitle={`${items.length} registros`}
      actions={
        <button data-testid="new-prueba-btn"
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 active:scale-95">
          <PlusIcon size={16} weight="bold" /> Nuevo registro
        </button>
      }
    >
      <div className="bg-card border border-border/60 rounded-lg p-5 mb-4 flex flex-col items-center gap-4">
        <button data-testid="buen-btn" onClick={() => setShowSun((v) => !v)}
          className="bg-agro-yellow text-black font-semibold rounded-md px-6 py-2 text-sm active:scale-95 hover:opacity-90">
          Buen
        </button>
        {showSun && <SunImage />}
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-x-auto" data-testid="prueba-table">
        {loading ? (
          <div className="p-8 text-center text-text-muted">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            <FlaskIcon size={32} className="inline mb-2 opacity-40" />
            <div>No hay registros todavía</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-wider text-text-secondary text-left">
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Descripción</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-border/40 hover:bg-app/50" data-testid={`prueba-row-${p.codigo}`}>
                  <td className="px-4 py-3 font-mono text-xs text-agro-green">{p.codigo}</td>
                  <td className="px-4 py-3 text-text-primary">{p.nombre}</td>
                  <td className="px-4 py-3 text-text-secondary">{p.descripcion}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${p.activo ? "text-agro-green border-agro-green/40 bg-agro-green/10" : "text-text-muted border-border bg-app"}`}>
                      {p.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => onEdit(p)} data-testid={`edit-prueba-${p.codigo}`}
                        className="text-xs py-1.5 px-3 border border-border rounded hover:border-agro-green/40 text-text-secondary hover:text-text-primary flex items-center gap-1.5">
                        <PencilSimpleIcon size={12} /> Editar
                      </button>
                      <button aria-label="Eliminar" onClick={() => onDel(p)} data-testid={`delete-prueba-${p.codigo}`}
                        className="text-xs py-1.5 px-3 border border-border rounded hover:border-red-400/40 text-text-muted hover:text-red-400">
                        <TrashIcon size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...pm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="prueba-modal">
            <div className="p-5 border-b border-border flex justify-between">
              <h3 className="font-heading text-lg font-semibold">{editing ? "Editar registro" : "Nuevo registro"}</h3>
              <button aria-label="Cerrar" onClick={pm.cerrar} className="text-text-muted"><XIcon size={20} /></button>
            </div>
            <form onSubmit={pm.guardar} className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Código *</label>
                <input required value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                  data-testid="form-prueba-codigo"
                  placeholder="PRB-001" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green font-mono uppercase" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Nombre *</label>
                <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  data-testid="form-prueba-nombre"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Descripción</label>
                <textarea rows={3} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  data-testid="form-prueba-descripcion"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  data-testid="form-prueba-activo" />
                Activo
              </label>
              <div className="flex justify-end gap-3 pt-4 border-t border-border/60 mt-2">
                <button type="button" onClick={pm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={pm.saving} type="submit" data-testid="form-prueba-submit"
                  className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover">
                  {editing ? "Guardar cambios" : "Crear registro"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
